<template>
    <div class="segment-page p-2" id="segment-page">
        <h2 class="mb-3">{{page_name}} {{page_name == 'Add' ? 'an' : 'the'}} Audience</h2>

        <div class="card p-0 mb-3">
            <div class="card-body">
                <div class="row">
                    <div class="col mb-2">
                        <h4 class="h6 fw-semibold mt-2 mb-0">Audience Source</h4>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-4 col-12 mb-3">
                        <label class="form-label">Audience Type</label>
                        <select class="form-select" name="type" @change="changeAudienceType()" v-model="local_segment.type" aria-label="Audience Type">
                            <option value="owned">Owned</option>
                            <option value="modeled">Modeled</option>
                        </select>
                    </div>
                    <div class="col-md-8 col-12 mb-3" v-if="local_segment.type === 'owned'">
                        <label class="form-label">Data Source</label>
                        <select class="form-select" name="source" @change="changeSource()" :disabled="(local_segment.id != null)"
                            v-model="local_segment.combo_source"  aria-label="Data Source">
                            <option v-for="s in sources" :value="s.value" :disabled="s.disabled" :class="{'text-muted': s.disabled}">{{propercase(s.text)}}</option>
                        </select>
                    </div>
                    <div class="col-md-8 col-12 mb-3" v-if="local_segment.type === 'modeled'">
                        <label class="form-label">Base Audience</label>
                        <select class="form-select" name="modeled_segment_id" v-model="local_segment.modeled_segment_id" aria-label="Base Audience">
                            <option value="">Select an owned audience...</option>
                            <option v-for="segment in ownedSegments" :value="segment.id">{{segment.name}}</option>
                        </select>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-8 col-12 mb-3" :class="((errors.name != '' )? 'is-invalid':'')">
                        <label class="form-label">Audience Name</label>
                        <input type="text" class="form-control" name="name"
                            v-model="local_segment.name"
                            :class="{'is-invalid': errors.name != ''}"  aria-label="Audience Name">
                        <div class="text-danger invalid-feedback" v-if="errors.name != ''">
                            {{errors.name }}
                        </div>
                    </div>
                    <div class="col-md-4 col-12 mb-3">
                        <label class="form-label">Expires At 
                            <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" data-placement="top" 
                            title="You can choose when to stop updating this audience in connected tools. Your audience will remain static in the destination after this date." alt=""></img>
                        </label>
                        <datepicker-utc 
                            v-model="local_segment.expires_at"
                            inputFormat="M/d/yyyy"
                            input-class="form-control"  
                            :customClass="((errors.date != '' )? 'is-invalid':'')"
                            position="right">
                        </datepicker-utc>
                    </div>
                </div>

                <div class="row">
                    <div class="col">
                        <label class="form-label">What would motivate this audience to take action? <em>(Optional)</em> 
                            <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" data-placement="top" title="By providing a motivation for this audience, you will be able to create content that is more relevant to them." alt=""></img>
                        </label>
                        <textarea class="form-control" v-model="local_segment.value_prop" rows="3" placeholder="Enter a couple of sentences that would be helpful to know when drafting copy for this audience"></textarea>
                    </div>
                </div>

            </div>
        </div>
        <div class="row d-flex align-items-stretch mb-3" v-if="local_segment.type === 'owned' && local_segment.data_source_id != null">
            <!-- Audience Definition card -->
            <div class="mb-3 position-relative" :class="is_expanded ? 'col-12' : 'col-xl-8 col-12'">
                <div class="card p-0 h-100">
                    <div class="card-body position-relative">
                        <div class="d-flex align-items-center justify-content-between mb-2">
                            <h4 class="h6 fw-semibold mt-2 mb-0">Audience Definition</h4>
                            <div class="d-none d-xl-flex gap-1 ms-2">
                                <button class="btn btn-secondary btn-icon-only-md me-2" @click="toggleExpand"
                                    data-bs-toggle="tooltip" title="Toggle full width">
                                    <img class="icon" :src="`/img/icons/avid-icons/${is_expanded ? 'minimize-4' : 'maximize-4'}.svg`" alt=""></img>
                                </button>
                                <button v-if="showRefineButton" type="button" class="btn btn-primary" @click="openSegmentRefineModal"
                                    data-bs-toggle="tooltip" title="Ask Edna how to change this segment">
                                    <img class="icon" src="/img/icons/avid-icons/edna.svg" style="width: 20px; height: 20px;" alt=""> Refine
                                </button>
                            </div>
                        </div>
                        <div class="position-relative">
                            <div :class="{'is-invalid': errors.value != ''}" >
                                <query-builder :client="client" v-if="local_segment.data_source_id != null" v-model="local_segment.filter" :include_trans="true"
                                :data_source_id="local_segment.data_source_id" :source="local_segment.source" :in_card="false"></query-builder>
                            </div>
                            <div class="text-danger invalid-feedback mb-2" v-if="errors.value != ''">
                                {{errors.value }}
                            </div>

                            <!-- Segment size (FilterDefinitionCard-style) -->
                            <div v-if="segment_size !== null && segment_size >= 0" class="segment-proposal-contact-count-wrap mt-3">
                                <div class="segment-proposal-contact-count d-flex align-items-center">
                                    <i class="fas fa-users me-2"></i>
                                    <span><strong>{{ number_with_zero(segment_size) }} contacts</strong> will be included in this audience.</span>
                                </div>
                            </div>
                            <div v-else-if="segment_size === -1" class="segment-proposal-contact-count-wrap mt-3">
                                <div class="segment-proposal-contact-count d-flex align-items-center">
                                    <i class="fas fa-exclamation-triangle me-2"></i>
                                    <span>Unable to calculate contact count.</span>
                                </div>
                            </div>
                            <div v-else-if="segment_size === null" class="segment-proposal-contact-count-wrap mt-3">
                                <div class="segment-proposal-contact-count d-flex align-items-center text-muted">
                                    <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                                    <span>Calculating contact count...</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Preview Column Values card -->
            <div class="d-none d-xl-block mb-3" :class="is_expanded ? 'd-none' : 'col-xl-4 col-12'">
                <div class="card p-0 h-100">
                    <div class="card-body">
                        <h4 class="h6 fw-semibold mt-2 mb-3">Preview Column Values</h4>
                        <preview-table :client="client" :source="local_segment"
                            :source_columns="db_columns" :data_type="null" :single-card="true"></preview-table>
                    </div>
                </div>
            </div>
        </div>
        <div class="card p-0 mb-4" v-if="isConstituentDataBlend && local_segment.type === 'owned'">
            <div class="card-body">
                <h4 class="h6 fw-semibold mt-2 mb-3">Make this into an Analysis Audience</h4>
                <p>The <em><a v-if="analysisReportUrl" :href="analysisReportUrl" target="_blank">Analysis Audience Giving Report <i class="fa fa-external-link-alt"></i></a><span v-else>Analysis Audience Giving Report</span></em> in Insights has the ability to compare two or more audiences. This is useful when comparing year over year performance of an appeal or understanding the differences between donors from different appeals.</p>
                
                <div class="form-check mt-3">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="createAnalysisAudience" 
                        v-model="local_segment.create_analysis_audience"
                        :disabled="!canCreateAnalysisAudience"
                     aria-label="Checkbox">
                    <label class="form-check-label" for="createAnalysisAudience">
                        Create an Analysis Audience
                    </label>
                    <div class="form-text" v-if="!canCreateAnalysisAudience">
                        <em>(This option is only available when you select columns from the Transactions section in the dropdown above.)</em>
                    </div>
                </div>
            </div>
        </div>

        <div class="row mt-2" v-if="form.error">
            <div class="col">
                <div class="alert alert-danger">
                    There was an issue saving your audience. Check the fields above to get more information.
                </div>
            </div>
        </div>
        <div class="mb-4 mt-2">
            <button type="button" class="btn btn-primary save-button me-2 box-shadowed" :disabled="form.saving"
                @click.prevent="saveSegment();">
                <span v-if="form.saving">
                    <div class="spinner-border spinner-border-sm float-left" role="status"> <span class="visually-hidden">Saving...</span></div> 
                    Saving
                </span>
                <span v-else>
                    <img class="icon" src="/img/icons/avid-icons/save.svg" alt=""> Save
                </span>
            </button>
            <button type="button" class="btn btn-white box-shadowed" @click="goBack()" :disabled="form.saving">
                <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> Cancel
            </button>
        </div>

        <SegmentRefineModal
            ref="segmentRefineModalRef"
            :client="client"
            @refine-submitted="onRefineSubmitted"
        />
        <SegmentRefineProposalModal
            ref="segmentRefineProposalModalRef"
            :client="client"
            @apply="onRefineApply"
        />
    </div>
</template>

<script>
    import moment from 'moment';
    import Swal from 'sweetalert2';
    import { defineComponent, defineAsyncComponent } from 'vue';
    import { router } from '@inertiajs/vue3';
    import { useNotifications } from '../../composables/useNotifications';
    import SegmentRefineModal from './SegmentRefineModal.vue';
    import SegmentRefineProposalModal from './SegmentRefineProposalModal.vue';

    const PreviewTable = defineAsyncComponent(() =>
        import('./PreviewTable.vue').then((module) => module.default)
    );

    export default defineComponent({
        components: {
            Swal,
            SegmentRefineModal,
            SegmentRefineProposalModal,
            PreviewTable,
        },
        setup() {
            const { showNotification } = useNotifications();
            return {
                showNotification
            };
        },
        props: {
            client: { type: Object },
            segment: { type: Object },
            datasets: { type: Array },
            datablends: { type: Array }
        },
        data() {
            return {
                form: {
                    saving: false,
                    error: false
                },
                errors :{
                    name: "",
                    value: ""
                },
                local_segment:{},
                disable_dates: {
                    to: new Date(), // Disable all dates up to today
                },
                db_columns: [],
                segment_size: null,
                is_expanded: false,

                //analysis audience modal
                modal_action: '',
                segmentSizeTimeout: null,
                analysisReportUrl: null,

                // Segment Refine flow
                pendingRefineSuggestionId: null,
            };
        },
        beforeMount() { 
            // Create initial segment object with all required properties
            const initialSegment = {
                ...this.segment,
                type: this.segment.type || 'owned',
                modeled_segment_id: this.segment.base_segment?.id || null,
                create_analysis_audience: this.segment.analysis ? true : false,
                filter: this.segment.filter || {
                    logic_units: [],
                    filter_logic: '',
                    filter_ui: 'simple',
                    filter_option: 'custom',
                    logic_units_modification: 0
                }
            };
            
            // Handle URL parameters
            const params = new URLSearchParams(window.location.search);
            if(params.get('dataset') != null){
                const dataset_id = parseInt(params.get('dataset'));
                initialSegment.source = 'data_set';
                initialSegment.data_source_id = dataset_id;
            } else if(params.get('datablend') != null){
                const datablend_id = parseInt(params.get('datablend'));
                initialSegment.source = 'data_blend';
                initialSegment.data_source_id = datablend_id;
            } else if(!initialSegment.id){
                // Sort data blends and datasets alphabetically
                const sortedDataBlends = this.datablends ? [...this.datablends].sort((a, b) => 
                    (a.type + " Data Blend").localeCompare(b.type + " Data Blend")) : [];
                const sortedDatasets = this.datasets ? [...this.datasets].sort((a, b) => 
                    a.name.localeCompare(b.name)) : [];

                // Default to first data blend if available, otherwise first dataset
                if (sortedDataBlends.length > 0) {
                    initialSegment.source = 'data_blend';
                    initialSegment.data_source_id = sortedDataBlends[0].id;
                } else if (sortedDatasets.length > 0) {
                    initialSegment.source = 'data_set';
                    initialSegment.data_source_id = sortedDatasets[0].id;
                }
            }

            // Set data source object
            initialSegment.data_source = {
                data_source: initialSegment.source,
                type: initialSegment.data_source?.type || null,
                id: initialSegment.data_source_id
            };

            // Set combo source
            initialSegment.combo_source = `${initialSegment.source}-${initialSegment.data_source_id}`;
            
            // Assign the complete object to local_segment
            this.local_segment = initialSegment;
            
            // Load database columns after setup
            this.loadDatabaseColumns();
            
            // Get segment size for all segments (including new ones)
            this.getSegmentSize();
            
            // Fetch the analysis report URL
            this.fetchAnalysisReportUrl();
        },
        computed: {
            sources() {
                var sources = [];
                var dataBlends = [];
                var dataSets = [];

                if(this.datablends != null) {
                    for(var i = 0; i < this.datablends.length; i++) {
                        dataBlends.push({
                            text: this.datablends[i].type + " Data Blend", 
                            value: 'data_blend-' + this.datablends[i].id
                        });
                    }
                }
                
                if(this.datasets != null) {
                    for(var i = 0; i < this.datasets.length; i++) {
                        dataSets.push({
                            text: this.datasets[i].name, 
                            value: 'data_set-' + this.datasets[i].id
                        });
                    }
                }

                // Sort both arrays alphabetically by text
                dataBlends.sort((a, b) => a.text.localeCompare(b.text));
                dataSets.sort((a, b) => a.text.localeCompare(b.text));

                // Add data blends first
                sources = sources.concat(dataBlends);

                // Add divider if we have both types
                if (dataBlends.length > 0 && dataSets.length > 0) {
                    sources.push({ text: '──────────', value: 'divider', disabled: true });
                }

                // Add data sets
                sources = sources.concat(dataSets);

                return sources;
            },
            page_name() {
                if(this.local_segment.id == null || this.local_segment.id <= 0)
                    return 'Add';
                //If the URL contains the word "Clone"
                else if(window.location.href.indexOf('clone') > -1)
                    return 'Copy';
                else
                    return 'Edit';
            },
            isConstituentDataBlend() {
                // Check if it's a data blend and get the data blend type
                if (this.local_segment.source !== 'data_blend') {
                    return false;
                }
                
                // Find the data blend by ID to get its type
                const dataBlend = this.datablends?.find(db => db.id == this.local_segment.data_source_id);
                return dataBlend?.type === 'constituent';
            },
            canCreateAnalysisAudience() {
                if (!this.isConstituentDataBlend) {
                    return false;
                }
                
                // Check if any selected columns start with "transactions."
                if (!this.local_segment.filter?.logic_units) {
                    return false;
                }
                
                return this.local_segment.filter.logic_units.some(unit => {
                    if (!unit.db_column?.value) {
                        return false;
                    }
                    return unit.db_column.value.toLowerCase().startsWith('transactions.');
                });
            },
            ownedSegments() {
                if (!this.client.segments) return [];
                return this.client.segments
                    .filter(segment => (segment.type || 'owned') === 'owned')
                    .sort((a, b) => a.name.localeCompare(b.name));
            },
            showRefineButton() {
                return this.local_segment.type === 'owned'
                    && this.local_segment.data_source_id
                    && this.local_segment.source === 'data_blend';
            }
        },
        methods: {
            loadDatabaseColumns() {
                let data = {
                    client: this.client,
                    include_trans: true,
                    source: this.local_segment.data_source ? this.local_segment.data_source.data_source : this.local_segment.source,
                    data_source_id: this.local_segment.data_source_id,
                };
                window.axios.post('/api/bigquery/get_db_columns', data)
                .then(response => {
                    this.db_columns = response.data.columns;
                });
            },
            changeSource() {
                var parts = this.local_segment.combo_source.split('-');
                // Create a new object to avoid direct mutation
                const newSegment = {
                    ...this.local_segment,
                    source: parts[0],
                    data_source_id: parts[1],
                    data_source: {
                        data_source: parts[0],
                        id: parts[1]
                    }
                };
                
                // Update the local segment with the new object
                this.local_segment = newSegment;
                
                // Load database columns after the update
                this.loadDatabaseColumns();
                
                // Update segment size for all segments
                this.getSegmentSize();
            },
            changeAudienceType() {
                // Reset modeled segment selection when switching types
                if (this.local_segment.type === 'owned') {
                    this.local_segment.modeled_segment_id = null;
                } else if (this.local_segment.type === 'modeled') {
                    // Clear data source related fields for modeled segments
                    this.local_segment.source = null;
                    this.local_segment.data_source_id = null;
                    this.local_segment.combo_source = null;
                    this.local_segment.filter = {
                        logic_units: [],
                        filter_logic: '',
                        filter_ui: 'simple',
                        filter_option: 'custom',
                        logic_units_modification: 0
                    };
                }
            },
            saveSegment() {
                // Reset errors first
                this.errors = {
                    name: "",
                    value: ""
                };
                this.form.error = false;

                // Validate filter logic only for owned segments
                if (this.local_segment.type === 'owned' && !this.filterLogicIsValid(this.local_segment.filter)) {
                    this.form.error = true;
                }

                // Validate modeled segment selection
                if (this.local_segment.type === 'modeled' && !this.local_segment.modeled_segment_id) {
                    this.errors.value = 'Please select a base audience for modeled segments';
                    this.form.error = true;
                }

                // Verify they have named the segment
                if(!this.local_segment.name || this.local_segment.name.trim() == '') {
                    this.errors.name = 'Please enter a name for the segment';
                    this.form.error = true;
                }
                else
                    this.errors.name = "";

                if(this.form.error) {
                    this.form.saving = false;
                    return;
                }

                var self = this;
                this.form.saving = true;

                // Just perform the save - lookup was already done on page load if needed
                this.performSave();
            },



            async performSave() {
                const self = this;
                
                // Prepare the segment data for saving
                const segmentData = { ...this.local_segment };
                
                // For modeled segments, set data_source_id to the selected segment
                if (segmentData.type === 'modeled') {
                    segmentData.data_source_id = segmentData.modeled_segment_id;
                    // Remove the temporary modeled_segment_id field
                    delete segmentData.modeled_segment_id;
                }
                
                if(this.local_segment.id == null) {
                    try {
                        const response = await window.axios.post('/api/segment/'+this.client.url, segmentData);
                        self.form.saving = false;
                        
                        if(response.status == 200) {
                            self.local_segment = response.data.data;
                            // Update segment size with the saved segment
                            self.getSegmentSize();
                            Swal.fire({
                                title: "Success!",
                                html: "The segment name <strong>"+response.data.data.name +"</strong> was successfully created. You'll be able to push this segment to other integrated tools and use it as a filter for analysis. You will be directed back to the previous page you were on",
                                icon: "success",
                                customClass: {
                                    popup: 'swal-success'
                                },
                            }).then((result) => {
                                if (result.isConfirmed) {
                                    self.goBack();
                                }
                            });
                        }
                    } catch (error) {
                        self.form.error = true;
                        self.form.saving = false;

                        // Restore modeled_segment_id if save failed for modeled segments
                        if (self.local_segment.type === 'modeled' && segmentData.data_source_id) {
                            self.local_segment.modeled_segment_id = segmentData.data_source_id;
                        }

                        Swal.fire({
                            title: "Error!",
                            text: "There was an issue saving your audience. Please try again.",
                            icon: "error",
                            customClass: {
                                popup: 'swal-error'
                            },
                        });
                    }
                } else {
                    try {
                        const response = await window.axios.put('/api/segment/'+this.client.url + '/' + this.local_segment.id, segmentData);
                        self.form.saving = false;

                        if(response.status == 200) {
                            // Update segment size with the updated segment
                            self.getSegmentSize();
                            Swal.fire({
                                title: "Success!",
                                html: "The audience was successfully updated. On the next sync, the updated data will be pushed into any connected systems. You will be directed back to the previous page you were on",
                                icon: "success",
                                customClass: {
                                    popup: 'swal-success'
                                }
                            }).then((result) => {
                                if (result.isConfirmed) {
                                    self.goBack();
                                }
                            });
                        }
                    } catch (error) {
                        self.form.error = true;
                        self.form.saving = false;

                        // Restore modeled_segment_id if save failed for modeled segments
                        if (self.local_segment.type === 'modeled' && segmentData.data_source_id) {
                            self.local_segment.modeled_segment_id = segmentData.data_source_id;
                        }

                        Swal.fire({
                            title: "Error!",
                            text: "There was an issue saving your audience. Please try again.",
                            icon: "error",
                            customClass: {
                                popup: 'swal-error'
                            },
                        });
                    }
                }
            },



            isFileOperator(operator) {
                if (!operator) return false;
                
                const operatorValue = typeof operator === 'object' ? operator.value : operator;
                return operatorValue === 'IN_FILE' || operatorValue === 'NOT_IN_FILE';
            },
            filterLogicIsValid(filter) {
                var is_valid = true;

                //First reset the errors
                this.errors.value = "";

                //If they didn't enter any logic
                if(filter == "" || filter == null){
                    this.errors.value = "Please select a column and value to use with the filter."
                    is_valid = false;
                }
                //If they left one of the inputs blank in the logic units
                else {
                    var logic_units = filter.logic_units;
                    for(var i = 0; i < logic_units.length; i++) {
                        if(logic_units[i].db_column == "" || logic_units[i].db_column == null || logic_units[i].db_column.value == ""
                            || logic_units[i].operator == undefined 
                            || ( typeof logic_units[i].operator === 'string' && logic_units[i].operator == "" )
                            || ( logic_units[i].operator instanceof Object && logic_units[i].operator.value == "" )
                            || (logic_units[i].value == "" && logic_units[i].operator.value.indexOf("NULL") == -1)
                            ){
                            is_valid = false;
                        }
                    }
                }
                return is_valid;
            },
            
            goBack() {
                if (window.history.length > 1) {
                    window.history.back();
                    return;
                }

                router.visit('/pathways/' + this.client.url + '/manage_audiences');
            },
            number_with_zero(num) {
                if (num === null || num === undefined) return '0';
                return num.toLocaleString();
            },
            getSegmentSize() {
                // Only get segment size if we have a valid filter
                if (!this.filterLogicIsValid(this.local_segment.filter)) {
                    this.segment_size = -1;
                    return;
                }

                // Don't calculate size if we don't have a data source
                if (!this.local_segment.data_source_id || !this.local_segment.source) {
                    this.segment_size = null;
                    return;
                }

                // Don't calculate if filter is empty or has no logic units
                if (!this.local_segment.filter || !this.local_segment.filter.logic_units || this.local_segment.filter.logic_units.length === 0) {
                    this.segment_size = -1;
                    return;
                }

                this.segment_size = null; // Show loading state

                let form_data = {
                    filter: this.local_segment.filter,
                    source: this.local_segment.source,
                    data_source_id: this.local_segment.data_source_id
                };

                window.axios.post('/api/segment/' + this.client.url + '/segment_size', form_data)
                    .then(response => {
                        this.segment_size = response.data.size;
                    })
                    .catch(error => {
                        console.error('Error getting segment size:', error);
                        // Set to -1 to show "Needs More Info" message
                        this.segment_size = -1;
                    });
            },
            toggleExpand() {
                this.is_expanded = !this.is_expanded;
            },
            fetchAnalysisReportUrl() {
                // Fetch the Analysis Audience Giving Report URL dynamically
                const pageName = 'Analysis Audience Giving Report';
                window.axios.get(`/api/insights/page/${this.client.url}/${encodeURIComponent(pageName)}`)
                    .then(response => {
                        if (response.data.page && response.data.page.url) {
                            this.analysisReportUrl = `/insights/${this.client.url}/${response.data.page.url}`;
                        }
                    })
                    .catch(error => {
                        console.error('Error fetching analysis report URL:', error);
                        // If the page doesn't exist, analysisReportUrl will remain null
                    });
            },
            openSegmentRefineModal() {
                this.$refs.segmentRefineModalRef?.show(this.local_segment);
            },
            onRefineSubmitted({ segment_suggestion_id }) {
                this.pendingRefineSuggestionId = segment_suggestion_id;
                this.showNotification({
                    icon: '/img/icons/nav-icons/Edna.svg',
                    text: "We're refining your segment. You'll be notified when it's ready.",
                    type: 'info',
                    noDismiss: true,
                });
            },
            onRefineApply(filter) {
                this.local_segment.filter = filter;
                this.getSegmentSize();
            },
            handleOpenSegmentProposal(e) {
                const id = e.detail?.segment_suggestion_id;
                if (id != null && id === this.pendingRefineSuggestionId) {
                    this.pendingRefineSuggestionId = null;
                    this.$refs.segmentRefineProposalModalRef?.show(id);
                }
            }
        },
        mounted() {
            window.addEventListener('openSegmentProposal', this.handleOpenSegmentProposal);
        },
        beforeUnmount() {
            window.removeEventListener('openSegmentProposal', this.handleOpenSegmentProposal);
            // Clear the segment size timeout to prevent memory leaks
            if (this.segmentSizeTimeout) {
                clearTimeout(this.segmentSizeTimeout);
            }
        },
        watch: {
            'local_segment.filter': {
                handler(newFilter, oldFilter) {
                    // Debounce the segment size calculation to avoid too many API calls
                    if (this.segmentSizeTimeout) {
                        clearTimeout(this.segmentSizeTimeout);
                    }
                    
                    this.segmentSizeTimeout = setTimeout(() => {
                        this.getSegmentSize();
                    }, 1000); // Wait 1 second after filter changes
                },
                deep: true
            },
            canCreateAnalysisAudience: {
                handler(newValue, oldValue) {
                    // Reset the checkbox if conditions are no longer met
                    if (oldValue && !newValue && this.local_segment.create_analysis_audience) {
                        this.local_segment.create_analysis_audience = false;
                    }
                },
                immediate: true
            },
            'local_segment.analysis': {
                handler(newValue) {
                    // Update checkbox state when analysis relationship changes
                    this.local_segment.create_analysis_audience = !!newValue;
                },
                immediate: true
            }
        },
    })
</script>